import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RocketLaunchIcon } from "@heroicons/react/24/outline";
import ClaudeDesktopSetup from "../components/ClaudeDesktopSetup";
import { apiKeyService, APIKeyResponse } from "../services/apiKeyService";
import { useAuth } from "../context/AuthContext";

const ClaudeInstructions: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [createdKey, setCreatedKey] = useState<APIKeyResponse | null>(null);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoFilledKey, setAutoFilledKey] = useState<string | null>(null);

  // Check for API key from Account Management page
  useEffect(() => {
    const keyFromSession = sessionStorage.getItem("latestApiKey");
    if (keyFromSession) {
      setAutoFilledKey(keyFromSession);
      sessionStorage.removeItem("latestApiKey");
      // Scroll to setup section
      setTimeout(() => {
        const element = document.getElementById("claude-setup-section");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, []);

  // Clear created key after a timeout
  useEffect(() => {
    if (createdKey) {
      const timeout = setTimeout(() => {
        setCreatedKey(null);
      }, 300000); // 5 minutes
      return () => clearTimeout(timeout);
    }
  }, [createdKey]);

  const handleQuickGenerate = async () => {
    setIsGeneratingKey(true);
    setError(null);

    try {
      // Generate a timestamped name for the API key
      const timestamp = new Date().toISOString().split("T")[0];
      const response = await apiKeyService.createAPIKey({
        name: `Claude Desktop - ${timestamp}`,
      });
      setCreatedKey(response);

      // Scroll to the setup section
      setTimeout(() => {
        const element = document.getElementById("claude-setup-section");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create API key");
    } finally {
      setIsGeneratingKey(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <RocketLaunchIcon className="h-8 w-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              Claude Desktop Integration
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            Connect Claude Desktop to Always Obvious for AI-powered strategic
            insights
          </p>
        </div>

        {/* Auto-filled Key Notification */}
        {autoFilledKey && !createdKey && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <span className="text-2xl">✨</span>
            <div>
              <p className="text-green-800 font-medium">
                Your API key from Account Management has been automatically
                loaded!
              </p>
              <p className="text-green-700 text-sm mt-1">
                Scroll down to continue with the setup instructions.
              </p>
            </div>
          </div>
        )}

        {/* Benefits Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Why Use Claude Desktop with Always Obvious?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-3">
                <span className="text-2xl flex-shrink-0">🎯</span>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Strategic Analysis
                  </h3>
                  <p className="text-sm text-gray-600">
                    Get AI-powered insights about your segments, propositions,
                    and competitive positioning
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-2xl flex-shrink-0">📊</span>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Data-Driven Decisions
                  </h3>
                  <p className="text-sm text-gray-600">
                    Access your organization&apos;s data directly within Claude
                    for informed strategic planning
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-2xl flex-shrink-0">🚀</span>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Accelerated Planning
                  </h3>
                  <p className="text-sm text-gray-600">
                    Create OKRs, hypotheses, and account plans faster with AI
                    assistance
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-2xl flex-shrink-0">🔄</span>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Seamless Workflow
                  </h3>
                  <p className="text-sm text-gray-600">
                    Work with your Always Obvious data without switching between
                    applications
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Instructions */}
        <div
          id="claude-setup-section"
          className="bg-white rounded-lg shadow-md"
        >
          <div className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">
              Setup Instructions
            </h2>
            <p className="text-gray-600 mt-1">
              Follow these steps to connect Claude Desktop to Always Obvious
            </p>
          </div>
          <ClaudeDesktopSetup
            latestApiKey={createdKey?.key || autoFilledKey || null}
            onApiKeyUsed={() => {
              setCreatedKey(null);
              setAutoFilledKey(null);
            }}
            onQuickGenerate={handleQuickGenerate}
            isGeneratingKey={isGeneratingKey}
            quickGenerateError={error}
          />
        </div>

        {/* Help Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            Need Help?
          </h3>
          <div className="space-y-2 text-sm text-blue-800">
            <p>
              • <strong>API Key Management:</strong> Visit{" "}
              <button
                onClick={() => navigate("/account")}
                className="underline hover:text-blue-900"
              >
                Account Management
              </button>{" "}
              to view, revoke, or delete your API keys
            </p>
            <p>
              • <strong>Support:</strong> Contact your administrator if you
              encounter any issues
            </p>
            <p>
              • <strong>Documentation:</strong> Check out our knowledge base for
              detailed guides
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaudeInstructions;
